let audioContext = null;
let userInteracted = false;
let gestureListenersBound = false;

const BUSINESS_AUDIO_SETTINGS_KEY = "elsa_business_audio_settings";
const BUSINESS_AUDIO_SETTINGS_EVENT = "elsa-business-audio-settings-change";
const USER_GESTURE_EVENTS = ["pointerdown", "keydown", "touchstart"];

const DEFAULT_BUSINESS_AUDIO_SETTINGS = {
  enabled: false,
  notifications: false,
  pos: true,
  kitchen: true,
  production: true,
};

const BUSINESS_AUDIO_ALERT_CHANNELS = {
  notification_unread: "notifications",
  pos_cart_add: "pos",
  kitchen_new_order: "kitchen",
  production_confirmed: "production",
};

const getAudioContext = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const AudioContextClass =
    window.AudioContext || window.webkitAudioContext || null;

  if (!AudioContextClass) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  return audioContext;
};

const notifySettingsChanged = (settings) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(BUSINESS_AUDIO_SETTINGS_EVENT, { detail: settings })
  );
};

export const getBusinessAudioSettings = () => {
  if (typeof window === "undefined") {
    return { ...DEFAULT_BUSINESS_AUDIO_SETTINGS };
  }

  try {
    const rawSettings = window.localStorage.getItem(
      BUSINESS_AUDIO_SETTINGS_KEY
    );
    if (!rawSettings) {
      return { ...DEFAULT_BUSINESS_AUDIO_SETTINGS };
    }

    const parsedSettings = JSON.parse(rawSettings);
    return {
      ...DEFAULT_BUSINESS_AUDIO_SETTINGS,
      ...(parsedSettings && typeof parsedSettings === "object"
        ? parsedSettings
        : {}),
    };
  } catch (error) {
    return { ...DEFAULT_BUSINESS_AUDIO_SETTINGS };
  }
};

export const saveBusinessAudioSettings = (nextSettings) => {
  const settings = {
    ...DEFAULT_BUSINESS_AUDIO_SETTINGS,
    ...getBusinessAudioSettings(),
    ...nextSettings,
  };

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      BUSINESS_AUDIO_SETTINGS_KEY,
      JSON.stringify(settings)
    );
  }

  notifySettingsChanged(settings);
  return settings;
};

export const subscribeToBusinessAudioSettings = (callback) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleSettingsChange = (event) => {
    callback(event.detail || getBusinessAudioSettings());
  };

  window.addEventListener(BUSINESS_AUDIO_SETTINGS_EVENT, handleSettingsChange);
  window.addEventListener("storage", handleSettingsChange);

  return () => {
    window.removeEventListener(
      BUSINESS_AUDIO_SETTINGS_EVENT,
      handleSettingsChange
    );
    window.removeEventListener("storage", handleSettingsChange);
  };
};

function handleUserGesture() {
  userInteracted = true;
}

export const bindBusinessAudioGestureListeners = () => {
  if (typeof window === "undefined" || gestureListenersBound) {
    return;
  }

  USER_GESTURE_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleUserGesture, {
      capture: true,
      passive: true,
    });
  });
  gestureListenersBound = true;
};

bindBusinessAudioGestureListeners();

export const isBusinessAudioAlertAllowed = (alertType) => {
  const settings = getBusinessAudioSettings();
  const channel = BUSINESS_AUDIO_ALERT_CHANNELS[alertType];

  return Boolean(settings.enabled && channel && settings[channel]);
};

export const ensureRestaurantAudioReady = async () => {
  if (!userInteracted) {
    return null;
  }

  const context = getAudioContext();
  if (!context) {
    return null;
  }

  if (context.state === "suspended") {
    try {
      await context.resume();
    } catch (error) {
      return null;
    }
  }

  if (context.state === "running") {
    return context;
  }

  return null;
};

export const enableBusinessAudioAlerts = async (nextSettings = {}) => {
  userInteracted = true;
  const settings = saveBusinessAudioSettings({
    ...nextSettings,
    enabled: true,
  });
  await ensureRestaurantAudioReady();
  return settings;
};

export const disableBusinessAudioAlerts = () =>
  saveBusinessAudioSettings({ enabled: false });

export const primeRestaurantAudio = () => {
  bindBusinessAudioGestureListeners();
};

const scheduleTone = (
  context,
  {
    startTime,
    duration,
    frequency,
    type = "sine",
    peakGain = 0.04,
    attack = 0.01,
    release = 0.08,
  }
) => {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  const safeDuration = Math.max(duration, 0.05);
  const safeAttack = Math.min(Math.max(attack, 0.002), safeDuration / 2);
  const safeRelease = Math.min(Math.max(release, 0.02), safeDuration / 2);
  const sustainEnd = Math.max(
    startTime + safeDuration - safeRelease,
    startTime + safeAttack
  );

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);

  gainNode.gain.setValueAtTime(0.0001, startTime);
  gainNode.gain.exponentialRampToValueAtTime(peakGain, startTime + safeAttack);
  gainNode.gain.setValueAtTime(peakGain, sustainEnd);
  gainNode.gain.exponentialRampToValueAtTime(
    0.0001,
    startTime + safeDuration
  );

  oscillator.connect(gainNode);
  gainNode.connect(context.destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + safeDuration + 0.02);
};

const playPattern = async (tones) => {
  const context = await ensureRestaurantAudioReady();
  if (!context) {
    return false;
  }

  let cursor = context.currentTime + 0.02;
  tones.forEach((tone) => {
    cursor += tone.delayBefore || 0;
    scheduleTone(context, {
      ...tone,
      startTime: cursor,
    });
    cursor += Math.max(tone.duration || 0, 0.05);
  });

  return true;
};

const playKitchenOrderAlertPattern = () =>
  playPattern([
    {
      frequency: 784,
      type: "triangle",
      duration: 0.1,
      peakGain: 0.035,
    },
    {
      delayBefore: 0.05,
      frequency: 988,
      type: "triangle",
      duration: 0.12,
      peakGain: 0.04,
    },
    {
      delayBefore: 0.06,
      frequency: 1174,
      type: "sine",
      duration: 0.18,
      peakGain: 0.045,
      release: 0.12,
    },
  ]);

const playProductionConfirmedAlertPattern = () =>
  playPattern([
    {
      frequency: 659,
      type: "sine",
      duration: 0.12,
      peakGain: 0.03,
    },
    {
      delayBefore: 0.07,
      frequency: 880,
      type: "triangle",
      duration: 0.22,
      peakGain: 0.04,
      release: 0.16,
    },
  ]);

const playPosCartAddAlertPattern = () =>
  playPattern([
    {
      frequency: 392,
      type: "sine",
      duration: 0.08,
      peakGain: 0.018,
      attack: 0.008,
      release: 0.06,
    },
    {
      delayBefore: 0.04,
      frequency: 523.25,
      type: "triangle",
      duration: 0.1,
      peakGain: 0.024,
      attack: 0.008,
      release: 0.08,
    },
  ]);

const playNotificationBellAlertPattern = () =>
  playPattern([
    {
      frequency: 659.25,
      type: "triangle",
      duration: 0.08,
      peakGain: 0.022,
      attack: 0.008,
      release: 0.05,
    },
    {
      delayBefore: 0.03,
      frequency: 880,
      type: "sine",
      duration: 0.1,
      peakGain: 0.028,
      attack: 0.008,
      release: 0.07,
    },
    {
      delayBefore: 0.03,
      frequency: 1046.5,
      type: "triangle",
      duration: 0.14,
      peakGain: 0.032,
      attack: 0.01,
      release: 0.1,
    },
  ]);

export const playBusinessAudioAlert = (alertType) => {
  if (!isBusinessAudioAlertAllowed(alertType)) {
    return Promise.resolve(false);
  }

  switch (alertType) {
    case "notification_unread":
      return playNotificationBellAlertPattern();
    case "pos_cart_add":
      return playPosCartAddAlertPattern();
    case "kitchen_new_order":
      return playKitchenOrderAlertPattern();
    case "production_confirmed":
      return playProductionConfirmedAlertPattern();
    default:
      return Promise.resolve(false);
  }
};

export const playKitchenOrderAlert = () =>
  playBusinessAudioAlert("kitchen_new_order");

export const playProductionConfirmedAlert = () =>
  playBusinessAudioAlert("production_confirmed");

export const playPosCartAddAlert = () => playBusinessAudioAlert("pos_cart_add");

export const playNotificationBellAlert = () =>
  playBusinessAudioAlert("notification_unread");
